import { PhotoOutlined, ShoppingCartOutlined } from '@mui/icons-material'
import { PropertyTab, useClientProperty } from '../_main/contexts'
import React, { useEffect, useMemo } from 'react'
import { useGalleryAssignment, useGalleryProduct, useGalleryVirtualVisit, useGalleryVisualSelection, useGalleryVisualType, useGalleryVisualsMeta } from 'components/pages/Gallery/_main/contexts'

import { AssignmentStage } from 'constants/assignment'
import AutoFixNormalOutlinedIcon from '@mui/icons-material/AutoFixNormalOutlined'
import Box from '@mui/material/Box'
import { ClientAssignmentReference } from '../ClientAssignmentReference'
import { ClientGalleryOrderTypeFilter } from 'components/pages/Gallery/_main/ClientGallery/ClientGalleryOrderTypeFilter'
import { ClientGalleryPhoto } from 'components/pages/Gallery/_main/ClientGallery/ClientGalleryPhoto'
import { ClientGalleryUploadFloatingToolBar } from 'components/pages/Gallery/_main/ClientGallery/ClientGalleryFloatingToolBar'
import { ClientGalleryVirtualVisit } from 'components/pages/Gallery/_main/ClientGallery/ClientGalleryVirtualVisit'
import { ClientPropertySelfUploadPopup } from '../ClientPropertySelfUploadPopup/ClientPropertySelfUploadPopup.module'
import { EmptyContent } from 'components/common/EmptyContent'
import { GRAY_800 } from 'constants/styling/theme'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import { MUIButton } from 'components/common/MUIButton'
import { PageContent } from 'components/common/Page/PageContent'
import { Path } from 'constants/router'
import Stack from '@mui/material/Stack'
import { useClientGallery } from 'components/pages/Gallery/_main/ClientGallery/_main'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

/**
 * Client Property Gallery content.
 *
 * @example <ClientPropertyVisualsGallery />
 */
export const ClientPropertyVisualsGallery: React.FC = () => {
  const { t } = useTranslation(['gallery', 'ai_editing_empty_content'])
  const navigate = useNavigate()

  const { assignmentStage } = useGalleryAssignment()
  const { normalizedOriginalType } = useGalleryVisualType()
  const { isVirtualVisit, isAIEditingProduct } = useGalleryProduct()
  const { virtualVisitURL } = useGalleryVirtualVisit()
  const { purchasedVisualsExist } = useGalleryVisualsMeta()
  const { selectAllPurchased } = useGalleryVisualSelection()

  const {
    mode,
    isToolBarOpen,
    setIsToolBarOpen,
    isUploadPopupOpened,
    setIsUploadPopupOpened
  } = useClientGallery()

  const {
    propertyId,
    propertyAssignmentsData,
    propertyData,
    handleTabChange,
    setActiveTab,
    isSelectedPropertyAssignmentCancelled,
  } = useClientProperty()

  const isVisualSendToClient = useMemo(() => assignmentStage === AssignmentStage.VISUALS_SENT_TO_CLIENT, [assignmentStage])

  useEffect(() => {
    setIsToolBarOpen(true)

    // Run this effect only once to set the toolbar open state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Stack>
      <Stack width="100%" alignItems="center" justifyContent="center" >
        <PageContent>


          {/* ASSIGNMENT TYPES FILTERS */}
          {!!propertyAssignmentsData &&
            <ClientGalleryOrderTypeFilter
              isNonDocumentProduct
              missions={propertyAssignmentsData}
              onClick={(selectedAssignmentId) => navigate(Path.PROPERTY_VISUALS_GALLERY_ASSIGNMENT.replace(':id', propertyId).replace(':assignmentId', selectedAssignmentId))}
              dealAssignmentsForTab={propertyAssignmentsData}
            />
          }

          {isSelectedPropertyAssignmentCancelled &&
            <Stack width="100%" gap={4} display="flex" flexDirection="row" justifyContent="space-between" paddingBottom="4rem">
              <Stack marginTop="3.2rem" width="100%" position="relative" direction="row">
                <Stack position="absolute" width="100%">
                  <ClientAssignmentReference />
                </Stack>

                <Box maxWidth="60rem" width="100%" margin="auto" marginTop="8rem">
                  <EmptyContent
                    title={t('client_gallery.cancelled')}
                    icon={<ShoppingCartOutlined sx={{ color: GRAY_800, fontSize: '2.8rem' }} />}
                    button={
                      <MUIButton
                        sx={{ marginTop: '2.4rem' }}
                        onClick={() => {
                          setActiveTab(PropertyTab.ORDERS)
                          handleTabChange(PropertyTab.ORDERS)
                        }}
                      >
                        {t('client_gallery.cancelled_action')}
                      </MUIButton>
                    }
                  />
                </Box>
              </Stack>
            </Stack>
          }

          {!isSelectedPropertyAssignmentCancelled &&
            <Stack width="100%" gap={4} display="flex" flexDirection="row" justifyContent="space-between" paddingBottom="4rem">

              {/** ASSIGNMENT NOT DELIVERED TO CLIENT */}
              {!isVisualSendToClient && (
                <Stack marginTop="3.2rem" width="100%" position="relative" direction="row">
                  <Stack position="absolute" width="100%">
                    <ClientAssignmentReference />
                  </Stack>

                  <Box maxWidth="60rem" width="100%" margin="auto" marginTop="8rem">
                    {isAIEditingProduct && assignmentStage === AssignmentStage.VISUALS_SENT_BY_BKBN_TO_EDITOR ? (
                      <EmptyContent
                        title={t('ai_editing_empty_content:title')}
                        description={t('ai_editing_empty_content:description')}
                        icon={<AutoFixNormalOutlinedIcon sx={{ color: GRAY_800, fontSize: '2.8rem' }} />}
                        button={
                          <MUIButton
                            type="secondaryBorder"
                            sx={{ marginTop: '2.4rem' }}
                            onClick={() => navigate(Path.INDEX)}
                            startIcon={<HomeOutlinedIcon sx={{ color: GRAY_800, fontSize: '2rem' }} />}
                          >
                            {t('ai_editing_empty_content:button')}
                          </MUIButton>
                        }
                      />
                    ) : (
                      <EmptyContent
                        title={t('client_gallery.empty')}
                        icon={<PhotoOutlined sx={{ color: GRAY_800, fontSize: '2.8rem' }} />}
                        button={
                          <MUIButton
                            sx={{ marginTop: '2.4rem' }}
                            onClick={() => {
                              setActiveTab(PropertyTab.ORDERS)
                              handleTabChange(PropertyTab.ORDERS)
                            }}
                          >
                            {t('client_gallery.empty_action')}
                          </MUIButton>
                        }
                      />
                    )}
                  </Box>
                </Stack>
              )}

              {/** ASSIGNMENT DELIVERED TO CLIENT. Photo, staging, video... etc */}
              {isVisualSendToClient && !isVirtualVisit && <ClientGalleryPhoto propertyData={propertyData} />}

              {/** ASSIGNMENT DELIVERED TO CLIENT. Matterport - Virtual visit */}
              {isVisualSendToClient && isVirtualVisit && !!virtualVisitURL &&
                <Stack marginTop="3.2rem" width="100%" position="relative" display="flex">
                  <ClientAssignmentReference />

                  <ClientGalleryVirtualVisit />
                </Stack>
              }


            </Stack>
          }

          <ClientGalleryUploadFloatingToolBar
            mode={mode}
            isToolBarOpen={isToolBarOpen}
            isDownload={purchasedVisualsExist && isVisualSendToClient}
            setIsUploadPopupOpened={setIsUploadPopupOpened}
            selectAllPurchased={selectAllPurchased}
          />

          {isUploadPopupOpened &&
            <ClientPropertySelfUploadPopup
              isUploadPopupOpened={isUploadPopupOpened}
              setIsUploadPopupOpened={setIsUploadPopupOpened}
              normalizedOriginalType={normalizedOriginalType}
            />
          }

        </PageContent>

      </Stack>
    </Stack>
  )
}
